import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import Icon from 'flow/components/ui-components/Icon';
import SearchInput from 'flow/components/ui-components/SearchInput';
import { segmentTrack } from 'flow/actions/segment-actions';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import useKeyboardShortcuts from 'flow/hooks/useKeyboardShortcuts';
import { normalizeShortcutToDisplay } from 'flow/utils/shortcut-utils';
import { getKeyboardShortcuts } from 'flow/selectors/keyboard-shortcuts-selectors';
import { selectHasTicketsAdvancedSearch } from 'flow/selectors/feature-flag-selectors';
import AdvancedSearchPanelContent from './AdvancedSearchPanelContent';
import { FILTER_POPUP_MARKER_ATTR } from './constants';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var TicketsSearchInputSection = function TicketsSearchInputSection(_ref) {
  var onSearchQueryChange = _ref.onSearchQueryChange,
    onFiltersApply = _ref.onFiltersApply,
    appliedAdvancedSearchFilters = _ref.appliedAdvancedSearchFilters,
    hasActiveAdvancedFilters = _ref.hasActiveAdvancedFilters;
  var _useAppContext = useAppContext(),
    i18next = _useAppContext.i18next;
  var dispatch = useAppDispatch();
  var hasTicketsAdvancedSearch = useAppSelector(selectHasTicketsAdvancedSearch);
  var inputRef = useRef(null);
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    input = _useState2[0],
    setInput = _useState2[1];
  var _useKeyboardShortcuts = useKeyboardShortcuts(),
    callSearchShortcut = _useKeyboardShortcuts.callSearchShortcut;
  var shortcuts = useAppSelector(getKeyboardShortcuts);
  useEffect(function () {
    var handleKeyDown = function handleKeyDown(event) {
      if (callSearchShortcut('general', event)) {
        var _inputRef$current;
        event.preventDefault();
        (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return function () {
      return document.removeEventListener('keydown', handleKeyDown);
    };
  }, [callSearchShortcut]);
  var handleSearchQueryChange = useCallback(function (event) {
    var inputNew = event.target.value;
    if (!input && inputNew) {
      dispatch(segmentTrack(TICKET_STRINGS.SEARCH_BEGAN_TYPING, {
        component: 'Search bar'
      }));
    }
    setInput(inputNew);
    onSearchQueryChange(event);
  }, [dispatch, input, onSearchQueryChange]);
  var shortcutText = function shortcutText(type) {
    var matchedShortcut = shortcuts.find(function (shortcut) {
      return shortcut.get('enabled') && shortcut.get('label') === type;
    });
    if (!matchedShortcut) {
      return '';
    }
    var shortcutText = normalizeShortcutToDisplay(matchedShortcut === null || matchedShortcut === void 0 ? void 0 : matchedShortcut.get('value'));
    return "[".concat(shortcutText, "]");
  };
  var searchInput = /*#__PURE__*/_jsx(SearchInput, {
    "data-test-id": 'search-bar-tickets',
    iconKey: "search",
    placeholder: "".concat(i18next.t('input.search'), " ").concat(shortcutText('extension_list_search')),
    onChange: handleSearchQueryChange,
    ref: inputRef,
    className: hasTicketsAdvancedSearch ? 'pe-10' : undefined
  });
  if (!hasTicketsAdvancedSearch) {
    return /*#__PURE__*/_jsx("div", {
      className: "flex flex-col gap-y-2 px-3",
      children: /*#__PURE__*/_jsx("div", {
        className: "relative",
        children: searchInput
      })
    });
  }
  return /*#__PURE__*/_jsx("div", {
    className: "flex flex-col gap-y-2 px-3",
    children: /*#__PURE__*/_jsxs(Popover, {
      className: "relative z-10",
      children: [/*#__PURE__*/_jsxs("div", {
        className: "relative",
        children: [searchInput, /*#__PURE__*/_jsx(PopoverButton, {
          "aria-label": i18next.t('tickets.filter.advanced'),
          className: twMerge('absolute end-1 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-sm hover:bg-gray-300', hasActiveAdvancedFilters && 'bg-main text-iconOnBrand hover:darken-10'),
          children: /*#__PURE__*/_jsx(Icon, {
            iconKey: "setting_menu",
            size: "1.25rem"
          })
        })]
      }), /*#__PURE__*/_jsx(PopoverPanel, _objectSpread(_objectSpread({}, _defineProperty({}, FILTER_POPUP_MARKER_ATTR, '')), {}, {
        className: "shadow-base absolute inset-x-0 top-full z-20 mt-1 rounded-sm bg-white p-3",
        children: function children(_ref3) {
          var close = _ref3.close;
          return /*#__PURE__*/_jsx(AdvancedSearchPanelContent, {
            filters: appliedAdvancedSearchFilters,
            onApply: onFiltersApply,
            onClose: close
          });
        }
      }))]
    })
  });
};
export default TicketsSearchInputSection;